import { convertToCents, convertToDollars } from 'utility/numbers';

export class Product {
    constructor(params) {
        this.id = params.id;
        this.quantity = params.quantity || 0;
        this.price = params.price;
    }
}

export class Bundle {
    constructor(params) {
        this.id = params.id;
        this.quantity = params.quantity || 0;
        this.price = params.price;
        this.products = params.products || [];
    }
}

export function calculateOrder(cart, products, bundles, event, discount, paidTransaction) {
    // Number of paid products in cart
    const paidProductCount = cart.reduce((total, num) => {
        if (num.price > 0) {
            return total + num.qty * num.paidProductCount;
        } else {
            return total;
        }
    }, 0);

    const ourTaxRateMultiplier = 1.13;

    // ************** Sub Total ************** //

    const subTotal = Number(cart.reduce((total, num) => total + num.qty * num.price, 0).toFixed(2));

    let discountTotal = 0;
    let discountsApplied = 0;

    if (discount) {
        const orderBundles = bundles
            .filter((b) => b.qty > 0)
            .map((b) => new Bundle({ id: b.id, quantity: b.qty, price: b.price }));
        const orderProducts = products
            .filter((p) => p.qty > 0)
            .map((b) => new Product({ id: b.id, quantity: b.qty, price: b.price }));

        const lineItems = orderBundles.concat(orderProducts);

        const discResult = calculateDiscount({
            discPercent: discount.percent || 0,
            discAmount: discount.amount || 0,
            discProductMax: discount.productMax || null,
            discTicketMax: discount.ticketMax || null,
            ticketsDiscounted: discount.ticketsDiscounted || 0,
            discProducts: discount.products || [],
            perItemDiscount: Boolean(discount.perItemDiscount),
            lineItems: lineItems,
            orderBundles: orderBundles,
            orderProducts: orderProducts,
        });

        discountTotal = discResult.discountTotal;
        discountsApplied = discResult.discountsApplied;
    }

    // We have to subtract any discounts before calculating any fees on the subtotal
    const subTotalCalc = Number((subTotal - discountTotal).toFixed(2));

    // ************** Our Fees ************** //

    let serviceFee = 0,
        serviceFeeCalc = 0;

    if (paidTransaction) {
        const {
            feePerItem,
            feePerOrder,
            percentFeePerOrder,
            feeMinAmount,
            feeMaxAmount,
            taxableB4tFees,
        } = event.fees;

        const ourOrderPercent = cart.reduce((total, p) => {
            let feePerTicket = p.price * percentFeePerOrder;

            if (feePerTicket > 0) {
                if (feeMinAmount && feePerTicket < feeMinAmount) {
                    feePerTicket = feeMinAmount;
                }

                if (feeMaxAmount && feePerTicket > feeMaxAmount) {
                    feePerTicket = feeMaxAmount;
                }
            }

            const result = p.qty * feePerTicket;

            return total + result;
        }, 0);

        serviceFee = Number(
            (
                (paidProductCount * feePerItem + feePerOrder + ourOrderPercent) *
                (taxableB4tFees ? ourTaxRateMultiplier : 1)
            ).toFixed(2),
        );
        serviceFeeCalc = event.hostAbsorbsB4tFees ? 0 : serviceFee;
    }

    // ************** Taxes ************** //

    let tax = 0;

    if (paidTransaction) {
        const { taxable, taxIncluded } = event;
        const { taxRate } = event.fees;

        tax = taxable && !taxIncluded ? Number((subTotalCalc * taxRate).toFixed(2)) : 0;
    }

    // ************** CC Fees ************** //

    let creditCardFee = 0,
        creditCardFeeCalc = 0;

    if (paidTransaction) {
        const { ccFlatFee, ccPercent, taxableCcFees } = event.fees;

        if (event.hostAbsorbsCcFees) {
            // ** CC Fee is included in the ticket price ** //
            let _ccPercentFee = (subTotalCalc + tax + serviceFeeCalc) * ccPercent;
            let _ccFlatFee = ccFlatFee;

            creditCardFee = Number(
                ((_ccPercentFee + _ccFlatFee) * (taxableCcFees ? ourTaxRateMultiplier : 1)).toFixed(
                    2,
                ),
            );
            creditCardFeeCalc = 0;
        } else {
            // ** CC Fee is passed on to the customer ** //
            // Calc CC fee considering that increasing the final amount also increases the Stripe fee
            const originalCharge = subTotalCalc + tax + serviceFeeCalc;
            const adjustedCharge = (originalCharge + ccFlatFee) / (1 - ccPercent);

            let _ccPercentFee = adjustedCharge * ccPercent;
            let _ccFlatFee = ccFlatFee;

            creditCardFee = Number(
                ((_ccPercentFee + _ccFlatFee) * (taxableCcFees ? ourTaxRateMultiplier : 1)).toFixed(
                    2,
                ),
            );
            creditCardFeeCalc = creditCardFee;
        }
    }

    // ************** Totals ************** //

    const total = Number((subTotalCalc + serviceFeeCalc + tax + creditCardFeeCalc).toFixed(2));
    const unifiedFee = Number((serviceFeeCalc + creditCardFeeCalc).toFixed(2));

    return {
        subTotal,
        discountTotal,
        discountsApplied,
        paidTransaction,
        serviceFee,
        creditCardFee,
        tax,
        total,
        unifiedFee,
    };
}

export function calculateDiscount(params) {
    const { perItemDiscount, discProducts, lineItems, orderProducts } = params;

    const discPercent = Number((params.discPercent * 100).toFixed(0));
    const discAmount = Number((params.discAmount * 100).toFixed(0));
    const discProductMax = params.discProductMax ?? Infinity;
    const discTicketMax = params.discTicketMax
        ? params.discTicketMax - params.ticketsDiscounted
        : Infinity;

    // **** Internal State / Calcs **** //

    let discountTotal = 0;
    let discountsLeft = Math.min(discProductMax, discTicketMax);
    let discountsApplied = 0;

    const subtotal = Number(
        lineItems.reduce((total, i) => total + i.quantity * convertToCents(i.price), 0),
    );

    // **** ORDER LEVEL DISCOUNT  **** //

    if (perItemDiscount === false) {
        discountsApplied = 1;

        discountTotal += (subtotal * discPercent) / 100;
        discountTotal += discAmount;

        discountTotal = Math.min(discountTotal, subtotal);
    }

    // **** TICKET LEVEL DISCOUNT  **** //

    if (perItemDiscount === true) {
        // TICKET LEVEL - Discounts are appplied to individual tickets (excluding bundles)

        // Fixed Amount discounts are applied on every eligible ticket -- Capped at the price of each ticket
        // Host may limit the discount to a specific set of tickets
        // Host may limit # of tix that can be discounted -- by order and/or globally

        for (const product of orderProducts) {
            if (
                discProducts &&
                discProducts.length > 0 &&
                discProducts.includes(product.id) === false
            ) {
                continue; // This product is not eligible for discounting
            }

            const discountQuantity = Math.min(discountsLeft, product.quantity);

            // Calculate Discount
            let discountCalc =
                (convertToCents(product.price) * discountQuantity * discPercent) / 100;
            discountCalc += discountQuantity * Math.min(discAmount, convertToCents(product.price));

            // Update Totals
            discountTotal += discountCalc;
            discountsLeft -= discountQuantity;

            discountsApplied += discountQuantity;

            if (discountsLeft <= 0) {
                // We can't discount any more tickets
                break;
            }
        }
    }

    return {
        subtotal: convertToDollars(subtotal),
        discountTotal: convertToDollars(discountTotal),
        discountsLeft: discountsLeft === Infinity ? null : discountsLeft,
        discountsApplied: discountsApplied || null,
    };
}
